import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import ClassroomInsightsPreview from '../components/dashboard/ClassroomInsightsPreview';
import CustomisableLessonPlanPreview from '../components/dashboard/CustomisableLessonPlanPreview';
import {
  ClockIcon,
  AcademicCapIcon,
  BellAlertIcon,
  PresentationChartLineIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CursorArrowRaysIcon,
  UserGroupIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const ForEducators: React.FC = () => {
  const benefits = [
    {
      icon: ClockIcon,
      title: 'Save 10+ Hours Weekly',
      description: 'Automated marking, instant feedback, and AI-powered lesson planning give you back time to focus on teaching.',
    },
    {
      icon: BellAlertIcon,
      title: 'Early Intervention Alerts',
      description: 'Get notified the moment a student struggles, with specific recommendations for targeted support.',
    },
    {
      icon: ChartBarIcon,
      title: 'Live Progress Tracking',
      description: 'See exactly where each student stands across every competency in real-time, not just at report card time.',
    },
    {
      icon: PresentationChartLineIcon,
      title: 'Data-Driven Insights',
      description: 'Make informed decisions with comprehensive analytics on class performance, learning patterns, and growth trends.',
    },
    {
      icon: UserGroupIcon,
      title: 'Differentiated Learning',
      description: 'Automatically adjusts to each student\'s pace and level, ensuring everyone is appropriately challenged.',
    },
    {
      icon: SparklesIcon,
      title: 'AI Teaching Assistant',
      description: 'Get suggestions for lesson plans, extension activities, and intervention strategies based on your class data.',
    },
  ];

  const features = [
    {
      category: 'Progress Monitoring',
      items: [
        'Real-time student mastery tracking',
        'Class-wide performance overview',
        'Individual student deep-dives',
        'Intervention alerts for struggling students',
      ],
    },
    {
      category: 'Assessment & Feedback',
      items: [
        'Instant automated marking',
        'Detailed error analysis',
        'Formative assessment tools',
        'Progress reports generation',
      ],
    },
    {
      category: 'Curriculum Alignment',
      items: [
        'Australian curriculum mapped',
        'Cross-curricular connections visible',
        'Prerequisite tracking',
        'Learning outcome coverage',
      ],
    },
    {
      category: 'Lesson Management',
      items: [
        'Assign content to students or classes',
        'Set learning goals and targets',
        'Track completion and engagement',
        'Customise learning pathways',
      ],
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                Your Classroom <span className="text-gradient">Assistant</span>
              </h1>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                Real-time insights into student progress, automated assessment, and data-driven support.
                More time for what matters - teaching.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Save 10+ hours per week on admin tasks</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Instant visibility into every student's progress</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Evidence-based teaching recommendations</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                  Request School Demo
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                  See How It Works
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8">
                <div className="text-center mb-6">
                  <AcademicCapIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Teacher Dashboard</h3>
                  <p className="text-[var(--color-light-gray)]">Everything you need in one place</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Active Students</span>
                    <span className="text-[var(--color-electric-blue)] font-bold">28</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Avg. Mastery Level</span>
                    <span className="text-[var(--color-emerald)] font-bold">78%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Intervention Alerts</span>
                    <span className="text-[var(--color-amber)] font-bold">3</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Weekly Time Saved</span>
                    <span className="text-[var(--color-violet)] font-bold">12.5 hrs</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Why Teachers Love <span className="text-gradient">Lumen8</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Designed to support the way you teach
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <benefit.icon className="w-12 h-12 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p className="text-[var(--color-light-gray)]">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Comprehensive Teacher <span className="text-gradient">Tools</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Everything you need to run an efficient, data-driven classroom
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.category}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="glass-card p-8"
              >
                <h3 className="text-2xl font-bold text-white mb-6">{feature.category}</h3>
                <div className="space-y-3">
                  {feature.items.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--color-light-gray)]">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Teacher Dashboard Preview */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Your <span className="text-gradient">Teacher Dashboard</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Everything you need to monitor and support student progress in one place
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Classroom Insights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <ClassroomInsightsPreview />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Class Overview Dashboard</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">See all students' mastery levels at a glance</p>
                </div>
              </div>
            </motion.div>

            {/* Customisable Lesson Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <CustomisableLessonPlanPreview />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Customisable Lesson Plans</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">Build and adapt lessons with AI assistance</p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <CursorArrowRaysIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-6" />
            <h2 className="text-4xl font-black text-white mb-6">
              Ready to Transform Your <span className="text-gradient">Classroom?</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Join the education revolution. See how Lumen8 can make your teaching more effective, efficient, and enjoyable.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Book a School Demo
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                See How It Works
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default ForEducators;